import React from 'react'

import { redirect, type RouteObject } from 'react-router'

import Home, { homeLoader } from '@/routes/home'
import Root from '@/routes/root'

import ErrorPage from '@/components/base/error'
import { ListLoader } from '@/components/base/listLoader'
import Page, { ChildPage } from '@/components/base/page'
import LinkWithState from '@/components/base/link'
import {
    revalidateUnlessOnly,
    shellRoute,
    type RouteParts
} from '@/components/base/routeShell'
import { DetailsSkeleton } from '@/components/community/detailsSkeleton'
import { ProfileSkeleton } from '@/routes/community/peoples/profileSkeletone'
import * as Skeletons from '@/components/skeletons/screens'
import { LEADERBOARD_ENABLED } from '@/lib/flags'
import { warmChunk } from '@/lib/routeChunks'

import { Button } from '@/components/ui/button'
import { PlusCircle, Archive } from 'lucide-react'

type PageProps = Omit<React.ComponentProps<typeof Page>, 'children'>

const loadScheduleList = () => import('@/routes/schedule/list')

// Header actions load with their own small chunks; until then their slot
// keeps its size so the header does not shift.
const ICON_SLOT = <div className="h-9 w-9" aria-hidden="true" />
function lazyAction(load: () => Promise<{ default: React.ComponentType }>) {
    const Action = React.lazy(load)
    return (
        <React.Suspense fallback={ICON_SLOT}>
            <Action />
        </React.Suspense>
    )
}
const CommunityFilters = warmChunk(
    () => import('@/components/community/drawerFilters')
)
const CoachFilters = warmChunk(
    () => import('@/components/community/drawerCoachFilters')
)
const LeaderboardLink = () =>
    import('@/components/leaderboard/headerLink').then((m) => ({
        default: m.LeaderboardHeaderLink
    }))
if (LEADERBOARD_ENABLED) warmChunk(LeaderboardLink)
const Clock = warmChunk(() => import('@/components/base/clock'))

type Screen<M> = {
    path: string
    load: () => Promise<M>
    pick: (module: M) => RouteParts
    page: PageProps
    skeleton: React.ReactNode
    /**
     * Fetch the code ahead while idle: only the bottom-nav tabs and the
     * screen each one leads to. The rest load on first open, behind their
     * skeleton.
     */
    warm?: boolean
    shouldRevalidate?: RouteObject['shouldRevalidate']
}

/** A routed screen: opens at once in its page, skeleton until ready. */
function screen<M>({
    path,
    load,
    pick,
    page,
    skeleton,
    warm,
    shouldRevalidate
}: Screen<M>): RouteObject {
    return {
        path,
        ...shellRoute({
            load,
            pick,
            frame: (body) => <Page {...page}>{body}</Page>,
            skeleton,
            warm,
            shouldRevalidate
        }),
        errorElement: <ErrorPage />
    }
}

const back = { hasBackButton: true }

export function buildAppRoutes(): RouteObject[] {
    return [
        {
            path: '/',
            element: <Root />,
            errorElement: <ErrorPage />,
            children: [
                {
                    errorElement: <ErrorPage />,
                    children: [
                        {
                            index: true,
                            element: (
                                <Page>
                                    <Home />
                                </Page>
                            ),
                            loader: homeLoader,
                            errorElement: <ErrorPage />
                        },
                        screen({
                            path: 'onboarding',
                            load: () => import('@/routes/onboarding'),
                            pick: (m) => ({
                                Component: m.default,
                                loader: m.onboardingLoader
                            }),
                            page: { headerSlug: 'onboarding.title' },
                            skeleton: <Skeletons.ApplicationSkeleton />
                        }),
                        screen({
                            path: 'userProfile',
                            load: () => import('@/routes/user/profile'),
                            pick: (m) => ({
                                Component: m.default,
                                loader: m.editUserProfileLoader
                            }),
                            page: { ...back, headerSlug: 'profile.title' },
                            skeleton: <Skeletons.UserProfileSkeleton />
                        }),
                        screen({
                            path: 'userProfile/transactions',
                            load: () => import('@/routes/user/transactions'),
                            pick: (m) => ({
                                Component: m.default,
                                loader: m.userTransactionsLoader
                            }),
                            page: {
                                ...back,
                                headerSlug: 'profile.transactions.title'
                            },
                            skeleton: <Skeletons.FilteredRowsSkeleton />
                        }),
                        screen({
                            path: 'invite',
                            load: () => import('@/routes/invite'),
                            pick: (m) => ({
                                Component: m.default,
                                loader: m.inviteLoader
                            }),
                            page: {
                                ...back,
                                headerSlug: 'referral.invite.title'
                            },
                            skeleton: <Skeletons.InviteSkeleton />
                        }),
                        screen({
                            path: 'userProfile/reauth',
                            load: () => import('@/routes/user/reauth'),
                            pick: (m) => ({
                                Component: m.default,
                                loader: m.reAuthViewLoader
                            }),
                            page: {},
                            skeleton: <Skeletons.TextSkeleton />
                        }),
                        screen({
                            path: 'communityProgrammes/list',
                            load: () =>
                                import('@/routes/community/programmes/list'),
                            pick: (m) => ({
                                Component: m.default,
                                loader: m.communityProgrammesListLoader
                            }),
                            page: {
                                headerSlug: 'communityProgrammes.list.title',
                                headerAction: lazyAction(CommunityFilters)
                            },
                            skeleton: <ListLoader rows={4} />,
                            warm: true
                        }),
                        ...(LEADERBOARD_ENABLED
                            ? [
                                  screen({
                                      path: 'coaches/leaderboard',
                                      load: () =>
                                          import('@/routes/leaderboard'),
                                      pick: (m) => ({
                                          Component: m.default,
                                          loader: m.leaderboardLoader
                                      }),
                                      page: {
                                          ...back,
                                          headerSlug: 'leaderboard.title'
                                      },
                                      skeleton: (
                                          <Skeletons.LeaderboardSkeleton />
                                      )
                                  })
                              ]
                            : []),
                        screen({
                            path: 'coaches/list',
                            load: () =>
                                import('@/routes/community/coaches/list'),
                            pick: (m) => ({
                                Component: m.default,
                                loader: m.coachesListLoader
                            }),
                            page: {
                                ...back,
                                headerSlug: 'coaches.list.title',
                                headerAction: (
                                    <div className="flex items-center gap-2">
                                        {LEADERBOARD_ENABLED &&
                                            lazyAction(LeaderboardLink)}
                                        {lazyAction(CoachFilters)}
                                    </div>
                                )
                            },
                            skeleton: <ListLoader rows={4} />,
                            warm: true
                        }),
                        screen({
                            path: 'communityProgrammes/:programId/details',
                            load: () =>
                                import('@/routes/community/programmes/details'),
                            pick: (m) => ({
                                Component: m.default,
                                loader: m.communityProgrammesDetailsLoader
                            }),
                            page: {
                                ...back,
                                headerSlug: 'communityProgrammes.details.title'
                            },
                            skeleton: <DetailsSkeleton />,
                            warm: true
                        }),
                        screen({
                            path: 'coaches/:userId',
                            load: () =>
                                import('@/routes/community/peoples/profile'),
                            pick: (m) => ({
                                Component: m.default,
                                loader: m.communityProfileLoader
                            }),
                            page: {
                                ...back,
                                headerSlug: 'communityProgrammes.profile.title'
                            },
                            skeleton: <ProfileSkeleton />
                        }),
                        screen({
                            path: 'communityProgrammes/requestWorkout/:userId/',
                            load: () =>
                                import('@/routes/community/peoples/requestWorkout'),
                            pick: (m) => ({
                                Component: m.default,
                                loader: m.requestWorkoutLoader
                            }),
                            page: {
                                ...back,
                                headerSlug: 'community.workputRequest.title'
                            },
                            skeleton: <Skeletons.RequestWorkoutSkeleton />
                        }),
                        screen({
                            path: 'programmes/list/archived',
                            load: () =>
                                import('@/routes/programmes/archivedList'),
                            pick: (m) => ({
                                Component: m.default,
                                loader: m.archivedProgrammesListLoader,
                                action: m.archivedProgrammesListAction
                            }),
                            page: {
                                ...back,
                                headerSlug: 'programmes.archived.title'
                            },
                            skeleton: <Skeletons.FilteredRowsSkeleton />
                        }),
                        screen({
                            path: 'programmes/list',
                            load: () => import('@/routes/programmes/list'),
                            pick: (m) => ({
                                Component: m.default,
                                loader: m.programmesListLoader
                            }),
                            page: {
                                headerSlug: 'programmes.list.title.text',
                                headerAction: (
                                    <div className="flex items-center gap-1">
                                        <LinkWithState to="/programmes/list/archived">
                                            <Button variant="ghost" size="icon">
                                                <Archive className="h-5 w-5" />
                                            </Button>
                                        </LinkWithState>
                                        <LinkWithState to="/programmes/new/edit">
                                            <Button
                                                variant="secondary"
                                                size="icon"
                                            >
                                                <PlusCircle className="h-5 w-5" />
                                            </Button>
                                        </LinkWithState>
                                    </div>
                                )
                            },
                            skeleton: <ListLoader rows={3} />,
                            warm: true
                        }),
                        screen({
                            path: 'programmes/:programId/edit',
                            load: () => import('@/routes/programmes/edit'),
                            pick: (m) => ({
                                Component: m.default,
                                loader: m.editProgrammeLoader,
                                action: m.editProgrammeAction
                            }),
                            page: back,
                            skeleton: <Skeletons.EditProgrammeSkeleton />
                        }),
                        screen({
                            path: 'programmes/:programId/start',
                            load: () => import('@/routes/programmes/start'),
                            pick: (m) => ({
                                Component: m.default,
                                loader: m.startProgrammeLoader,
                                action: m.startProgrammeAction
                            }),
                            page: {
                                ...back,
                                headerSlug: 'programmes.list.field.id.title'
                            },
                            skeleton: <Skeletons.StartProgrammeSkeleton />
                        }),
                        screen({
                            path: 'programmes/:programId/stop',
                            load: () => import('@/routes/programmes/stop'),
                            pick: (m) => ({
                                Component: m.default,
                                loader: m.stopProgrammeLoader,
                                action: m.stopProgrammeAction
                            }),
                            page: {
                                ...back,
                                headerSlug: 'programmes.list.field.id.title'
                            },
                            skeleton: <Skeletons.StopProgrammeSkeleton />
                        }),
                        screen({
                            path: 'schedule/list',
                            load: loadScheduleList,
                            pick: (m) => ({
                                Component: m.default,
                                loader: m.scheduleListLoader
                            }),
                            page: {
                                headerSlug: 'schedule.list.field.id.title'
                            },
                            skeleton: <ListLoader rows={4} />,
                            warm: true
                        }),
                        screen({
                            path: 'schedule/list/:historyDate',
                            load: loadScheduleList,
                            pick: (m) => ({
                                Component: m.default,
                                loader: m.scheduleListLoader
                            }),
                            page: {
                                ...back,
                                headerSlug: 'schedule.list.history'
                            },
                            skeleton: <ListLoader rows={4} />
                        }),
                        screen({
                            path: 'schedule/:programId/details',
                            load: () => import('@/routes/schedule/details'),
                            pick: (m) => {
                                const Details = m.default
                                return {
                                    Component: () => (
                                        <Details startWeekOn="monday" />
                                    ),
                                    loader: m.detailsProgrammeLoader
                                }
                            },
                            page: {
                                ...back,
                                headerSlug: 'schedule.list.field.id.title',
                                headerAction: lazyAction(Clock)
                            },
                            skeleton: <Skeletons.ScheduleDetailsSkeleton />,
                            warm: true,
                            // A day tap moves within the data on screen.
                            shouldRevalidate: revalidateUnlessOnly('day')
                        }),
                        screen({
                            path: 'schedule/:programId/:scheduleId/reflect',
                            load: () => import('@/routes/training/reflection'),
                            pick: (m) => ({
                                Component: m.default,
                                loader: m.reflectionLoader,
                                action: m.reflectionAction
                            }),
                            page: {
                                ...back,
                                headerSlug: 'training.header.reflection.text'
                            },
                            skeleton: <Skeletons.ReflectionSkeleton />
                        }),
                        screen({
                            path: 'payments/subscription',
                            load: () =>
                                import('@/routes/payments/subscription'),
                            pick: (m) => ({ Component: m.default }),
                            page: {
                                ...back,
                                headerSlug: 'payments.subscription.header.text'
                            },
                            skeleton: <Skeletons.TextSkeleton />
                        }),
                        screen({
                            path: 'payments/limit/exceeded',
                            load: () =>
                                import('@/routes/payments/limitExceeded'),
                            pick: (m) => ({ Component: m.default }),
                            page: {
                                ...back,
                                headerSlug: 'payments.limitExceeded.header.text'
                            },
                            skeleton: <Skeletons.TextSkeleton />
                        }),
                        screen({
                            path: 'payments/transaction/success',
                            load: () =>
                                import('@/routes/payments/successfullTransaction'),
                            pick: (m) => ({ Component: m.default }),
                            page: back,
                            skeleton: <Skeletons.TextSkeleton />
                        }),
                        {
                            path: 'requests',
                            loader: () => redirect('/requests/list')
                        },
                        screen({
                            path: 'requests/list',
                            load: () => import('@/routes/requests/list'),
                            pick: (m) => ({
                                Component: m.default,
                                loader: m.requestsListLoader
                            }),
                            page: {
                                ...back,
                                headerSlug: 'requests.list.title'
                            },
                            skeleton: <Skeletons.FilteredRowsSkeleton />
                        }),
                        screen({
                            path: 'coach/apply',
                            load: () =>
                                import('@/routes/user/coachApplication'),
                            pick: (m) => ({
                                Component: m.default,
                                loader: m.coachApplicationLoader
                            }),
                            page: { ...back, headerSlug: 'coach.apply.title' },
                            skeleton: <Skeletons.ApplicationSkeleton />
                        }),
                        screen({
                            path: 'business/apply',
                            load: () =>
                                import('@/routes/user/businessApplication'),
                            pick: (m) => ({
                                Component: m.default,
                                loader: m.businessApplicationLoader
                            }),
                            page: {
                                ...back,
                                headerSlug: 'business.apply.title'
                            },
                            skeleton: <Skeletons.ApplicationSkeleton />
                        }),
                        screen({
                            path: 'requests/coach/settings',
                            load: () =>
                                import('@/routes/requests/coachSettings'),
                            pick: (m) => ({
                                Component: m.default,
                                loader: m.coachSettingsLoader
                            }),
                            page: {
                                ...back,
                                headerSlug: 'requests.coachSettings.title'
                            },
                            skeleton: <Skeletons.CoachSettingsSkeleton />
                        }),
                        screen({
                            path: 'requests/coach/payouts',
                            load: () =>
                                import('@/routes/requests/coachPayouts'),
                            pick: (m) => ({
                                Component: m.default,
                                loader: m.coachPayoutsLoader
                            }),
                            page: { ...back, headerSlug: 'coachPayouts.title' },
                            skeleton: <Skeletons.CoachDashboardSkeleton />
                        }),
                        screen({
                            path: 'requests/coach/analytics',
                            load: () =>
                                import('@/routes/requests/coachAnalytics'),
                            pick: (m) => ({
                                Component: m.default,
                                loader: m.coachAnalyticsLoader
                            }),
                            page: {
                                ...back,
                                headerSlug: 'coachAnalytics.title'
                            },
                            skeleton: <Skeletons.CoachDashboardSkeleton />
                        }),
                        screen({
                            path: 'requests/:id',
                            load: () => import('@/routes/requests/detail'),
                            pick: (m) => ({
                                Component: m.default,
                                loader: m.requestDetailLoader
                            }),
                            page: {
                                ...back,
                                headerSlug: 'requests.detail.title'
                            },
                            skeleton: <Skeletons.RequestDetailSkeleton />
                        }),
                        screen({
                            path: 'requests/:id/dispute',
                            load: () => import('@/routes/requests/dispute'),
                            pick: (m) => ({ Component: m.default }),
                            page: {
                                ...back,
                                headerSlug: 'requests.dispute.title'
                            },
                            skeleton: <Skeletons.ApplicationSkeleton />
                        }),
                        screen({
                            path: 'requests/:id/messages',
                            load: () => import('@/routes/requests/messages'),
                            pick: (m) => ({
                                Component: m.default,
                                loader: m.requestMessagesLoader
                            }),
                            page: {
                                ...back,
                                headerSlug: 'requests.messages.title'
                            },
                            skeleton: <Skeletons.MessagesSkeleton />
                        }),
                        {
                            path: 'dynamic',
                            element: <ChildPage headerSlug={' '} />,
                            children: [
                                {
                                    path: 'notification',
                                    ...shellRoute({
                                        load: () =>
                                            import('@/routes/dynamic/notification'),
                                        pick: (m) => ({ Component: m.default }),
                                        frame: (body) => body,
                                        skeleton: <Skeletons.TextSkeleton />
                                    }),
                                    errorElement: <ErrorPage />
                                },
                                {
                                    path: 'info/:slug',
                                    ...shellRoute({
                                        load: () =>
                                            import('@/routes/dynamic/info'),
                                        pick: (m) => ({
                                            Component: m.default,
                                            loader: m.infoLoader
                                        }),
                                        frame: (body) => body,
                                        skeleton: <Skeletons.TextSkeleton />
                                    }),
                                    errorElement: <ErrorPage />
                                }
                            ],
                            errorElement: <ErrorPage />
                        }
                    ]
                }
            ]
        }
    ]
}
